/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useState } from "react";
import { InvoiceResponse } from "../../../models/invoice/InvoiceResponse";
import { strings } from "../../../common/Strings/Strings";
import InvoiceApi from "../../../api/InvoiceApi";
import { getGeneralError } from "../../../util/helperFunctions";
import { InvoicingMethod } from "../../../models/invoice/InvoicingMethod";
import Modal from "../../../components/Modal/Modal";
import Button from "../../../components/Button";
import AlertBox, { AlertType } from "../../../components/AlertBox";

interface Props {
  close(): void;
  invoice: InvoiceResponse;
  isOpen: boolean;
  reload(): void;
}

const WithdrawInvoice = ({ invoice, isOpen, close, reload }: Props): ReactElement => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const cancelInvoice = async () => {
    setLoading(true);
    setError(null);

    try {
      await InvoiceApi.cancelInvoice(invoice.id);
      reload();
    } catch (err) {
      setError(await getGeneralError(err));
    } finally {
      setLoading(false);
    }
  };

  type WithdrawalState = {
    message: string;
    allowedToWithdraw: boolean;
  };

  const withdrawCostAccountingsToo = (): WithdrawalState => {
    if ((invoice.costAccounting?.historyOfCostAccounting?.length ?? 0) > 0) {
      if (invoice.invoicingMethod === InvoicingMethod.COST_CALCULATION) {
        return {
          message: strings.warningWithdrawCostAccountings,
          allowedToWithdraw: true,
        };
      } 
        return {
          message: strings.withdrawInvoiceNotPossible,
          allowedToWithdraw: false,
        };
      
    }
    return { message: "", allowedToWithdraw: true };
  };

  return (
    <Modal show={isOpen} handleClose={close} size="modal-md">
      <Modal.Header title={strings.withdrawInvoice} />
      <Modal.Body>
        <div className="text-base text-left space-y-4">
          <div>{`${strings.warningYouAreAboutToWithdrawAnInvoice} ${strings.areYouSure}`}</div>
          <AlertBox closeAble={false} message={withdrawCostAccountingsToo().message} type={AlertType.WARNING} />
          <AlertBox message={error} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close}>
          {strings.cancel}
        </Button>
        {withdrawCostAccountingsToo().allowedToWithdraw ? (
          <Button className="ml-auto" minW onClick={cancelInvoice} disabled={loading}>
            {strings.yes}
          </Button>
        ) : (
          <></>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WithdrawInvoice;
